/**
 * Ingesta por imagen (§4.5). Lógica compartida.
 *
 * El flujo tiene un solo camino y no se puede saltear ningún paso:
 *   foto → el servidor llama al modelo → JSON estricto → validación de rango → campos
 *   PRECARGADOS Y EDITABLES → confirmación del usuario → recién ahí se guarda.
 *
 * Nada se escribe antes de la confirmación. Y para el cardio, si el nivel de resistencia no salió
 * en la foto, se pide antes de dejar guardar: sin él los datos no son comparables (§5.4) y
 * guardarlo vacío es guardar una sesión que después no sirve para nada.
 *
 * El cardio cuelga de la sesión del día. Si no hay ninguna, confirmar la abre: hacer sólo cardio
 * es entrenar, y rechazar la lectura obligaba a salir a abrir la sesión perdiendo lo leído.
 *
 * Lo que esta pantalla NO es: offline-first. La lectura ocurre en el servidor, así que sin red no
 * hay nada guardado que suba después, y el mensaje lo dice con esas palabras.
 */

import { useCallback, useState } from 'react';
import {
  formatearDuracion,
  parsearDuracion,
  validarCardio,
  validarMedida,
  type Condiciones,
  type Hallazgo,
  type Resultado,
  type TipoIngesta,
  type Unidad,
} from '@mg/core';

import { ErrorAPI, pedir } from '../datos/api.ts';
import { abrirSesion, guardarCardio, guardarIngesta, guardarMedida } from './acciones.ts';
import { useDatos } from './useDatos.ts';

interface RespuestaOCR {
  tipo: TipoIngesta;
  lectura: { confianza_global: number; campos: Record<string, { valor: number | null; confianza: number }>; no_legibles: string[] };
  jsonCrudo: string;
  borrador: Record<string, unknown>;
  validacion: Resultado;
  camposParaRevisar: string[];
  modelo: string;
}

export interface CamposBalanza {
  peso: string;
  grasa_pct: string;
  masa_muscular: string;
  masa_magra: string;
  masa_osea: string;
  agua_pct: string;
  grasa_visceral: string;
  imc: string;
  tmb: string;
}

export interface CamposCardio {
  duracion: string;
  distancia_km: string;
  calorias: string;
  nivel_resistencia: string;
  maquina_tipo: string;
}

export type Paso = 'elegir' | 'leyendo' | 'revisar' | 'listo';

const s = (v: unknown): string => (v === null || v === undefined ? '' : String(v));
const num = (t: string): number | null => {
  const x = t.trim().replace(',', '.');
  if (x === '') return null;
  const n = Number(x);
  return Number.isFinite(n) ? n : null;
};

export function useIngesta() {
  const datos = useDatos();
  const [tipo, setTipo] = useState<TipoIngesta>('balanza');
  const [paso, setPaso] = useState<Paso>('elegir');
  const [error, setError] = useState<string | null>(null);
  /** El fallo fue de red: esta pantalla NO es offline-first y hay que decirlo. */
  const [sinRed, setSinRed] = useState(false);
  const [respuesta, setRespuesta] = useState<RespuestaOCR | null>(null);
  const [vistaPrevia, setVistaPrevia] = useState<string | null>(null);
  const [guardando, setGuardando] = useState(false);

  const [balanza, setBalanza] = useState<CamposBalanza | null>(null);
  const [cardio, setCardio] = useState<CamposCardio | null>(null);
  const [condiciones, setCondiciones] = useState<Condiciones>('desconocido');
  const [fecha, setFecha] = useState(datos.hoy);
  const [hora, setHora] = useState(() => new Date().toTimeString().slice(0, 5));
  const [confirmado, setConfirmado] = useState(false);

  const reiniciar = useCallback(() => {
    setPaso('elegir');
    setRespuesta(null);
    setVistaPrevia(null);
    setBalanza(null);
    setCardio(null);
    setError(null);
    setSinRed(false);
    setConfirmado(false);
  }, []);

  /** Reduce la foto antes de subirla: una imagen de 12 MP no mejora la lectura y tarda una eternidad. */
  const prepararImagen = (archivo: File): Promise<string> =>
    new Promise((resolver, rechazar) => {
      const lector = new FileReader();
      lector.onerror = () => rechazar(new Error('No se pudo leer la imagen.'));
      lector.onload = () => {
        const dataUri = String(lector.result);
        const img = new Image();
        img.onerror = () => resolver(dataUri);
        img.onload = () => {
          const maxLado = 1600;
          const escala = Math.min(1, maxLado / Math.max(img.width, img.height));
          if (escala >= 1) return resolver(dataUri);

          const lienzo = document.createElement('canvas');
          lienzo.width = Math.round(img.width * escala);
          lienzo.height = Math.round(img.height * escala);
          const ctx = lienzo.getContext('2d');
          if (!ctx) return resolver(dataUri);
          ctx.drawImage(img, 0, 0, lienzo.width, lienzo.height);
          resolver(lienzo.toDataURL('image/jpeg', 0.9));
        };
        img.src = dataUri;
      };
      lector.readAsDataURL(archivo);
    });

  const leer = useCallback(
    async (archivo: File): Promise<void> => {
      setError(null);
      setSinRed(false);
      setPaso('leyendo');
      try {
        const imagen = await prepararImagen(archivo);
        setVistaPrevia(imagen);

        const previa = datos.medidas[datos.medidas.length - 1];
        const r = await pedir<RespuestaOCR>('/ocr', {
          metodo: 'POST',
          timeoutMs: 60_000,
          cuerpo: {
            tipo,
            imagen,
            ...(tipo === 'balanza' && previa
              ? { medidaPrevia: { peso: previa.peso, unidad_peso: previa.unidad_peso } }
              : {}),
          },
        });

        setRespuesta(r);
        if (r.tipo === 'balanza') {
          const c = r.borrador['campos'] as Record<string, number | null>;
          setBalanza({
            peso: s(c['peso']),
            grasa_pct: s(c['grasa_pct']),
            masa_muscular: s(c['masa_muscular']),
            masa_magra: s(c['masa_magra']),
            masa_osea: s(c['masa_osea']),
            agua_pct: s(c['agua_pct']),
            grasa_visceral: s(c['grasa_visceral']),
            imc: s(c['imc']),
            tmb: s(c['tmb']),
          });
        } else {
          const c = r.borrador['campos'] as Record<string, number | null>;
          setCardio({
            duracion: c['duracion_seg'] ? formatearDuracion(c['duracion_seg']) : '',
            distancia_km: s(c['distancia_km']),
            calorias: s(c['calorias']),
            nivel_resistencia: s(c['nivel_resistencia']),
            maquina_tipo: 'elíptica',
          });
        }
        setPaso('revisar');
      } catch (e) {
        // Sin red el mensaje genérico del cliente HTTP ("queda guardado y sube después") sería
        // mentira: leer la foto ocurre en el servidor, así que no se guardó nada y la foto se
        // perdió. Se dice así, con la salida a cargarla a mano.
        const offline = e instanceof ErrorAPI && (e.codigo === 'sin_red' || e.codigo === 'timeout');
        setSinRed(offline);
        setError(
          offline
            ? 'Leer la foto necesita internet. No se guardó nada.'
            : e instanceof ErrorAPI
              ? e.message
              : 'No se pudo leer la imagen.',
        );
        setPaso('elegir');
      }
    },
    [tipo, datos.medidas],
  );

  // --- Validación de lo que el usuario tiene en pantalla ahora mismo -------

  const validacionActual: Resultado = (() => {
    if (tipo === 'balanza' && balanza) {
      const peso = num(balanza.peso);
      if (peso === null) return { ok: false, requiereConfirmacion: false, hallazgos: [] };
      const previa = datos.medidas.filter((m) => m.fecha < fecha).slice(-1)[0] ?? null;
      return validarMedida(
        {
          peso,
          unidad_peso: 'kg' as Unidad,
          grasa_pct: num(balanza.grasa_pct),
          masa_muscular: num(balanza.masa_muscular),
          masa_magra: num(balanza.masa_magra),
          masa_osea: num(balanza.masa_osea),
          agua_pct: num(balanza.agua_pct),
          grasa_visceral: num(balanza.grasa_visceral),
          imc: num(balanza.imc),
          tmb: num(balanza.tmb),
        },
        previa,
      );
    }

    if (tipo === 'consola_cardio' && cardio) {
      const dur = parsearDuracion(cardio.duracion);
      return validarCardio({
        duracion_seg: dur ?? 0,
        distancia_km: num(cardio.distancia_km) ?? 0,
        calorias: num(cardio.calorias) ?? 0,
        nivel_resistencia: num(cardio.nivel_resistencia),
      });
    }

    return { ok: false, requiereConfirmacion: false, hallazgos: [] };
  })();

  const errores: Hallazgo[] = validacionActual.hallazgos.filter((h) => h.severidad === 'error');
  const aConfirmar: Hallazgo[] = validacionActual.hallazgos.filter((h) => h.severidad === 'confirmar');

  /** §4.5.5 — sin nivel de resistencia no se deja guardar el cardio. */
  const faltaNivel = tipo === 'consola_cardio' && num(cardio?.nivel_resistencia ?? '') === null;

  /**
   * El cardio cuelga de la sesión del día y hoy puede no haber ninguna. No es un impedimento —se
   * abre sola al confirmar—, pero hay que avisarlo antes: descubrirlo recién al guardar dejaba la
   * lectura en un callejón, porque salir a abrir la sesión perdía lo que el modelo había leído.
   */
  const sesionDelCardio = datos.sesionEnCurso ?? datos.sesionDeHoy;
  const abriraSesion = tipo === 'consola_cardio' && sesionDelCardio === null;

  const puedeGuardar =
    validacionActual.ok && !faltaNivel && (!validacionActual.requiereConfirmacion || confirmado);

  const confirmar = useCallback(async (): Promise<boolean> => {
    if (!datos.perfil || !respuesta || !puedeGuardar) return false;
    setGuardando(true);
    setError(null);

    try {
      if (tipo === 'balanza' && balanza) {
        const medida = await guardarMedida({
          user_id: datos.perfil.id,
          fecha,
          hora,
          peso: num(balanza.peso)!,
          unidad_peso: 'kg',
          grasa_pct: num(balanza.grasa_pct),
          masa_muscular: num(balanza.masa_muscular),
          masa_magra: num(balanza.masa_magra),
          masa_osea: num(balanza.masa_osea),
          agua_pct: num(balanza.agua_pct),
          grasa_visceral: num(balanza.grasa_visceral),
          imc: num(balanza.imc),
          tmb: num(balanza.tmb),
          fuente: 'ocr',
          condiciones,
        });

        await guardarIngesta({
          userId: datos.perfil.id,
          tipo: 'balanza',
          jsonCrudo: respuesta.jsonCrudo,
          confianza: respuesta.lectura.confianza_global,
          medidaId: medida.id,
        });
      } else if (cardio) {
        // Una sesión sin día de rutina es legítima: hacer sólo cardio es entrenar. Así que en vez
        // de rechazar la lectura, se abre la sesión de hoy y el cardio queda adentro.
        let sesion = datos.sesionEnCurso ?? datos.sesionDeHoy;
        if (!sesion) {
          if (!datos.gimnasioActivo) {
            setError('Falta configurar tu gimnasio para poder abrir la sesión de hoy.');
            return false;
          }
          sesion = await abrirSesion({
            userId: datos.perfil.id,
            planId: datos.planActivo?.id ?? null,
            gimnasioId: datos.gimnasioActivo.id,
            rutinaDiaId: null,
            fecha: datos.hoy,
            ejerciciosPlanificados: [],
          });
        }

        const sesionId = sesion.id;
        const registro = await guardarCardio(
          sesionId,
          {
            maquina_tipo: cardio.maquina_tipo,
            duracion_seg: parsearDuracion(cardio.duracion) ?? 0,
            distancia_km: num(cardio.distancia_km) ?? 0,
            calorias: num(cardio.calorias) ?? 0,
            nivel_resistencia: num(cardio.nivel_resistencia),
            fuente: 'ocr',
          },
          datos.cardio.find((c) => c.sesion_id === sesionId) ?? null,
        );

        await guardarIngesta({
          userId: datos.perfil.id,
          tipo: 'consola_cardio',
          jsonCrudo: respuesta.jsonCrudo,
          confianza: respuesta.lectura.confianza_global,
          cardioId: registro.id,
        });
      }

      setPaso('listo');
      return true;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo guardar.');
      return false;
    } finally {
      setGuardando(false);
    }
  }, [datos, respuesta, puedeGuardar, tipo, balanza, cardio, fecha, hora, condiciones]);

  return {
    datos,
    tipo,
    setTipo,
    paso,
    error,
    respuesta,
    vistaPrevia,
    balanza,
    setBalanza,
    cardio,
    setCardio,
    condiciones,
    setCondiciones,
    fecha,
    setFecha,
    hora,
    setHora,
    confirmado,
    setConfirmado,
    errores,
    aConfirmar,
    faltaNivel,
    abriraSesion,
    puedeGuardar,
    guardando,
    sinRed,
    leer,
    confirmar,
    reiniciar,
    /** Campos que el modelo marcó como poco confiables. */
    revisar: new Set(respuesta?.camposParaRevisar ?? []),
    noLegibles: respuesta?.lectura.no_legibles ?? [],
  };
}
